import React, { useState } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';

/**
 * FECOA-1238: Amend the time windows of a LIVE schedule mid-study.
 *
 * Collects a required reason and posts the (already-edited) schedule config to
 * the versioned amend endpoint. The backend freezes every completed, in-progress,
 * or past window and regenerates only future windows, so historical completion
 * data is preserved by construction.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} scheduleId - the live Schedule UUID
 * @param {Object} config - the full new Schedule.config to apply
 * @param {(result: object) => void} [onAmended] - called with the amend summary
 */
const AmendScheduleModal = ({ open, onClose, scheduleId, config, onAmended }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('A reason is required to amend a live schedule.');
      return;
    }
    setLoading(true);
    try {
      const result = await studyBuilderService.amendSchedule(scheduleId, config, reason.trim());
      toast.success(
        `Schedule amended (v${result.version_number}). ` +
        `${result.windows_frozen} historical window(s) preserved, ` +
        `${result.windows_created} future window(s) regenerated.`
      );
      onAmended?.(result);
      onClose();
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.config ||
        err?.response?.data?.detail ||
        'Failed to amend schedule.';
      toast.error(Array.isArray(detail) ? detail[0] : detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white dark:bg-slate-900 rounded-lg w-full max-w-md p-6 shadow-xl">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Amend live schedule
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          This schedule has active participants. Amending it applies the new timing
          to <strong>future</strong> windows only. Completed, in-progress, and past
          windows and their responses are preserved unchanged.
        </p>

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Reason for amendment
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          placeholder="e.g. Sponsor protocol amendment v2 — morning dose moved to 09:00"
          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 dark:bg-slate-800 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />

        <div className="flex gap-2 mt-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-700 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Amending…' : 'Amend schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmendScheduleModal;
